import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useCallback, useState, useRef, useContext } from 'react';
import styled from 'styled-components';
import { DescendantsProvider } from "../../hooks/useDescendants";
import { getLastEnabledChildIndex, getFirstEnabledChildIndex, getNextEnabledIndex, getPrevEnabledIndex } from "../../hooks/useDescendants/traversal-helpers";
import { TabsContext } from "./TabsContext";
import { theme } from "../../theme";
import { TabSlider } from "./TabSlider";
import { getPanelId } from "./get-accessibility-ids";
import { focusWithoutScrolling } from '@react-aria/utils';
export function TabList(_ref) {
  var children = _ref.children,
      _ref$shouldRenderSlid = _ref.shouldRenderSlider,
      shouldRenderSlider = _ref$shouldRenderSlid === void 0 ? true : _ref$shouldRenderSlid,
      _ref$shouldManageTabO = _ref.shouldManageTabOrder,
      shouldManageTabOrder = _ref$shouldManageTabO === void 0 ? true : _ref$shouldManageTabO,
      other = _objectWithoutProperties(_ref, ["children", "shouldRenderSlider", "shouldManageTabOrder"]);

  var boundaryRef = useRef(null);

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      descendants = _useState2[0],
      setDescendants = _useState2[1];

  var _useContext = useContext(TabsContext),
      onChange = _useContext.onChange,
      value = _useContext.value,
      id = _useContext.id;
  /**
   * Enable switching between tabs with keyboard arrows
   */


  var handleKeyDown = useCallback(function (e) {
    function handleEvent() {
      e.stopPropagation();
      e.preventDefault();
    }

    switch (e.key) {
      case 'Tab':
        {
          if (!shouldManageTabOrder) {
            break;
          }

          var focusTargetId = getPanelId(id, value);
          var targetEl = document.getElementById(focusTargetId);

          if (targetEl) {
            handleEvent();
            targetEl.focus();
          }

          break;
        }

      case 'ArrowRight':
        {
          handleEvent();
          var nextIndex = getNextEnabledIndex(value, descendants);
          onChange(nextIndex);
          focusWithoutScrolling(descendants[nextIndex].element.current);
          break;
        }

      case 'ArrowLeft':
        {
          handleEvent();
          var prevIndex = getPrevEnabledIndex(value, descendants);
          onChange(prevIndex);
          focusWithoutScrolling(descendants[prevIndex].element.current);
          break;
        }

      case 'Home':
        {
          handleEvent();
          var firstEnabledChildIndex = getFirstEnabledChildIndex(descendants);
          var firstEnabledChild = descendants[firstEnabledChildIndex];

          if (firstEnabledChild) {
            onChange(firstEnabledChildIndex);
            focusWithoutScrolling(firstEnabledChild.element.current);
          }

          break;
        }

      case 'End':
        {
          handleEvent();
          var lastEnabledChildIndex = getLastEnabledChildIndex(descendants);
          var lastEnabledChild = descendants[lastEnabledChildIndex];

          if (lastEnabledChild) {
            onChange(lastEnabledChildIndex);
            focusWithoutScrolling(lastEnabledChild.element.current);
          }

          break;
        }

      default:
    }
  }, [shouldManageTabOrder, id, value, descendants, onChange]);
  var tabChildren = React.Children.count(children);
  return /*#__PURE__*/React.createElement(DescendantsProvider, {
    descendants: descendants,
    setDescendants: setDescendants
  }, /*#__PURE__*/React.createElement(TabContainer, _extends({
    ref: boundaryRef
  }, other), shouldRenderSlider && /*#__PURE__*/React.createElement(TabSlider, {
    style: {
      width: "calc(100% / ".concat(tabChildren, ")"),
      transform: "translateX(".concat(value * 100, "%)")
    }
  }), /*#__PURE__*/React.createElement(StyledTabList, {
    onKeyDown: handleKeyDown,
    role: "tablist"
  }, children)));
}
var TabContainer = styled.div.withConfig({
  displayName: "TabList__TabContainer",
  componentId: "vapor__sc-18lqen3-0"
})(["overflow-y:hidden;position:relative;background-color:", ";border-radius:", ";"], theme.colors.background.component, theme.radii[8]);
var StyledTabList = styled.div.withConfig({
  displayName: "TabList__StyledTabList",
  componentId: "vapor__sc-18lqen3-1"
})(["display:grid;grid-auto-columns:1fr;grid-auto-flow:column;outline:none;"]);